import React, { useId, useState } from "react";
import "./ui.css";

/**
 * Section – karta s hlavičkou pro logický blok stránky nebo formuláře.
 * SectionHeader – samostatný oddělovač (bez karty) uvnitř delšího formuláře.
 *
 * Čistě prezentační komponenty; jediný stav je otevřeno/zavřeno u sbalitelné
 * sekce (lze řídit i zvenčí přes `open` + `onOpenChange`).
 */

export type SectionTone = "default" | "warning" | "admin";

/** Úroveň nadpisu titulku – stránka má h1 v hlavičce AppShellu, sekce h2, pododdíly h3/h4. */
export type SectionHeadingLevel = 2 | 3 | 4;

export interface SectionProps {
  /** Titulek sekce (zobrazí se verzálkami v hlavičce karty). */
  title: string;
  /** Ikona z bootstrap-icons, např. "bi-info-circle" (ne emoji). Prefix "bi " je volitelný. */
  icon?: string;
  /** Doplňující popis pod titulkem. */
  description?: string;
  /** Akce vpravo v hlavičce (tlačítka, badge). */
  actions?: React.ReactNode;
  /** Titulek je tlačítko s aria-expanded a chevronem; zavřený obsah se nemountuje. */
  collapsible?: boolean;
  /** Výchozí stav sbalitelné sekce (nekontrolovaný režim). Výchozí true. */
  defaultOpen?: boolean;
  /** Kontrolovaný stav otevření – přebíjí defaultOpen (např. otevřít sekci s chybou). */
  open?: boolean;
  /** Volá se při kliknutí na titulek sbalitelné sekce. */
  onOpenChange?: (open: boolean) => void;
  /** Barevné ladění hlavičky: warning = režim editace, admin = administrace. */
  tone?: SectionTone;
  /** Úroveň nadpisu titulku. Výchozí 2. */
  headingLevel?: SectionHeadingLevel;
  /** id elementu <section>; odvozuje se z něj id obsahu pro aria-controls. */
  id?: string;
  className?: string;
  children?: React.ReactNode;
}

export interface SectionHeaderProps {
  title: string;
  /** Ikona z bootstrap-icons, např. "bi-calendar" (ne emoji). */
  icon?: string;
  description?: string;
  actions?: React.ReactNode;
  /** Úroveň nadpisu. Výchozí 3 (oddělovač uvnitř sekce / karty). */
  headingLevel?: SectionHeadingLevel;
  id?: string;
  className?: string;
}

/** Spojí třídy a vynechá prázdné hodnoty. */
const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(" ");

/** "bi-x", "bi bi-x" i "x" → "bi bi-x" */
const iconClass = (icon: string): string => {
  const name = icon.trim().replace(/^bi\s+/, "");
  return name.indexOf("bi-") === 0 ? `bi ${name}` : `bi bi-${name}`;
};

const headingTag = (level: SectionHeadingLevel): "h2" | "h3" | "h4" =>
  `h${level}` as "h2" | "h3" | "h4";

const TitleContent: React.FC<{ icon?: string; title: string }> = ({
  icon,
  title,
}) => (
  <>
    {icon && (
      <i
        className={cx(iconClass(icon), "lks-section__icon")}
        aria-hidden="true"
      />
    )}
    <span className="lks-section__text">{title}</span>
  </>
);

export const Section: React.FC<SectionProps> = ({
  title,
  icon,
  description,
  actions,
  collapsible = false,
  defaultOpen = true,
  open,
  onOpenChange,
  tone = "default",
  headingLevel = 2,
  id,
  className,
  children,
}) => {
  const autoId = useId();
  const sectionId = id || autoId;
  const bodyId = `${sectionId}-body`;

  const [innerOpen, setInnerOpen] = useState<boolean>(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = !collapsible || (isControlled ? open : innerOpen);

  const toggle = () => {
    const next = !isOpen;
    if (!isControlled) setInnerOpen(next);
    if (onOpenChange) onOpenChange(next);
  };

  const Heading = headingTag(headingLevel);

  return (
    <section
      id={id}
      className={cx(
        "card lks-section",
        tone !== "default" && `lks-section--${tone}`,
        className
      )}
    >
      <header className="card-header lks-section__header">
        <div className="lks-section__main">
          <Heading className="lks-section__title">
            {collapsible ? (
              <button
                type="button"
                className="lks-section__toggle"
                aria-expanded={isOpen}
                aria-controls={bodyId}
                onClick={toggle}
              >
                <TitleContent icon={icon} title={title} />
                <i
                  className="bi bi-chevron-down lks-section__chevron"
                  aria-hidden="true"
                />
              </button>
            ) : (
              <TitleContent icon={icon} title={title} />
            )}
          </Heading>
          {description && <p className="lks-section__desc">{description}</p>}
        </div>
        {actions && <div className="lks-section__actions">{actions}</div>}
      </header>
      {isOpen && (
        <div id={bodyId} className="card-body lks-section__body">
          {children}
        </div>
      )}
    </section>
  );
};

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  icon,
  description,
  actions,
  headingLevel = 3,
  id,
  className,
}) => {
  const Heading = headingTag(headingLevel);
  return (
    <div id={id} className={cx("lks-section-header", className)}>
      <Heading className="lks-section__title">
        <TitleContent icon={icon} title={title} />
      </Heading>
      {actions && <div className="lks-section__actions">{actions}</div>}
      {description && <p className="lks-section__desc">{description}</p>}
    </div>
  );
};

export default Section;
